import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import flutterVideo from '../../../images/flutter-app.mp4';
import styles from './Flutter.module.css';

const FlutterProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToIntroduction = () => {
    const introSection = document.querySelector('[data-section="introduction"]');
    if (introSection) {
      introSection.scrollIntoView({ behavior: 'smooth' });
    }
  };



  const responsibilities = [
    { text: 'User-Centered Design', icon: '👤' },
    { text: 'Information Architecture', icon: '🏗️' },
    { text: 'Visual Design', icon: '🎨' },
    { text: 'Interactive Prototyping', icon: '⚡' },
    { text: 'Collaboration with Development Team', icon: '🤝' },
    { text: 'Documentation and Design Guidelines', icon: '📋' },
    { text: 'Continuous Improvement', icon: '🔄' }
  ];

  const tools = [
    { name: 'Flutter', icon: '📱', description: 'Mobile Framework' },
    { name: 'Figma', icon: '🎨', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' }
  ];

  const skills = [
    'CSS', 'Figma', 'HTML5', 'Svelte', 'Flutter', 
    'CorelDraw', 'WordPress', 'JavaScript', 'Tailwind CSS', 'Adobe Animate'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background */}
      <div className={styles.backgroundGradient}>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <div className={`${styles.projectBadge} ${styles.closedProject}`}>
                <span className={styles.badgeDot}></span>
                <span>Closed Project</span>
              </div>
              
              <h1 className={styles.projectTitle}>Student Education App</h1>
              <p className={styles.projectSubtitle}>Student Education App</p>
              
              <div className={styles.projectMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Platform</span>
                  <span className={styles.metaValue}>Mobile App</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Year</span>
                  <span className={styles.metaValue}>2024</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Role</span>
                  <span className={styles.metaValue}>UI Designer</span>
                </div>
              </div>
              
              <p className={styles.heroDescription}>
                Designed a user-friendly interface for a student education app, focusing on intuitive navigation and engaging visual elements to enhance learning experiences through AI-powered features.
              </p>
              
              <div className={styles.ctaButtons}>
                <button className={styles.ctaPrimary} onClick={scrollToIntroduction}>Case Study</button>
              </div>
            </div>
            
            <div className={styles.heroRight}>
              <div className={styles.heroVisual}>
                <video 
                  className={styles.heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src={flutterVideo} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className={styles.section} data-section="introduction">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Introduction</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              As a UI designer, I had the opportunity to contribute to the development of a student education app that revolutionizes learning experiences. One of my key responsibilities was designing an intuitive and visually appealing interface for users to easily access classes, practice quizzes, AI chat functionality, and AI-powered search features. By implementing user-centered design principles and conducting extensive usability testing, I ensured that the app provided seamless navigation and engaging interactions. The result was a highly functional and aesthetically pleasing platform that enhances students' ability to acquire knowledge efficiently and effectively.
            </p>
          </div>
        </section>

        {/* My Role Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Role</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              As the UI designer for a student education app, my role was pivotal in shaping the user experience for key features such as class access, practice quizzes, AI chat, and AI search functionalities. I focused on creating intuitive and visually compelling interfaces that seamlessly integrated these features into the app's overall design. Leveraging my expertise in user-centered design principles and proficiency with design tools, I crafted user interfaces that facilitated easy navigation, clear information hierarchy, and engaging interactions. Through collaboration with the development team and incorporating feedback from user testing, I ensured that the design not only met functional requirements but also exceeded user expectations, enhancing the app's usability and appeal.
            </p>
          </div>
        </section>

        {/* Responsibilities Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Responsibilities</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.responsibilitiesGrid}>
            {responsibilities.map((responsibility, index) => (
              <div 
                key={index}
                className={styles.responsibilityCard}
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredResponsibility(index)}
                onMouseLeave={() => setHoveredResponsibility(null)}
              >
                <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                <div className={styles.cardIcon}>{responsibility.icon}</div>
                <div className={styles.cardText}>{responsibility.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tools Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Tools Used</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.toolsGrid}>
            {tools.map((tool, index) => (
              <div 
                key={index}
                className={styles.toolCard}
                style={{ animationDelay: `${index * 0.15}s` }}
                onMouseEnter={() => setHoveredTool(index)}
                onMouseLeave={() => setHoveredTool(null)}
              >
                <div className={styles.toolGlow}></div>
                <div className={styles.toolIcon}>{tool.icon}</div>
                <div className={styles.toolName}>{tool.name}</div>
                <div className={styles.toolDescription}>{tool.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.skillsContainer}>
            {skills.map((skill, index) => (
              <div 
                key={index}
                className={styles.skillTag}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <span className={styles.skillHash}>#</span>
                {skill}
              </div>
            ))}
          </div>
        </section>

        
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default FlutterProjectShowcase;